import { ArrowRight } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  getMemberOnboarding,
  type MemberOnboardingFlow,
  type MemberOnboardingStep,
  setOnboardingStep,
} from "@/features/clients/member-onboarding-api";

/** Per-step label + optional deep-link to where the work happens. The Team card
 * lives on this same Overview page, so the TL "assign" step just hints at it. */
const STEP_META: Record<string, { label: string; to?: (id: string) => string; hint?: string }> = {
  assign: { label: "Assign to staff", hint: "Use the Team card to assign members." },
  share_with_team: { label: "Share with the team" },
  start_work: { label: "Start work" },
  profile_assigned: { label: "Review the client profile", to: (id) => `/client/${id}/profile` },
  doc_check: { label: "Check required documents", to: (id) => `/client/${id}/required-docs` },
};

const ROLE_TITLE: Record<string, string> = {
  team_leader: "Team Leader onboarding",
  staff: "Staff onboarding",
};

/**
 * The assigned member's short "getting started" checklist for a client (Team
 * Leader: Assign → Share with team → Start work; Staff: Profile Assigned → Doc
 * Check → Start work). Self-hides when the caller has no flow here or has
 * finished it (the API returns `role: null` / a `completed_at`).
 */
export function ClientMemberOnboardingCard({ clientId }: { clientId: string }) {
  const [flow, setFlow] = useState<MemberOnboardingFlow | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    getMemberOnboarding(clientId)
      .then((f) => active && setFlow(f))
      .catch(() => {
        // Non-fatal — just don't show the card.
      });
    return () => {
      active = false;
    };
  }, [clientId]);

  if (!flow || !flow.role || flow.completed_at) return null;

  async function toggle(step: MemberOnboardingStep) {
    setBusy(step.key);
    try {
      const res = await setOnboardingStep(clientId, step.key, !step.done);
      setFlow((f) => (f ? { ...f, steps: res.steps, completed_at: res.completed_at } : f));
    } catch {
      // ignore; the checkbox stays as-is
    } finally {
      setBusy(null);
    }
  }

  const doneCount = flow.steps.filter((s) => s.done).length;

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between gap-2">
        <CardTitle className="text-base">{ROLE_TITLE[flow.role] ?? "Getting started"}</CardTitle>
        <span className="text-muted-foreground text-xs tabular-nums">
          {doneCount}/{flow.steps.length}
        </span>
      </CardHeader>
      <CardContent className="flex flex-col gap-1">
        {flow.steps.map((step) => {
          const meta = STEP_META[step.key] ?? { label: step.key };
          return (
            <div
              key={step.key}
              className="hover:bg-muted flex items-center gap-3 rounded-md px-2 py-2 text-sm"
            >
              {/* The label wraps ONLY the checkbox + text so the deep-link (a
                  sibling) never toggles the step. */}
              <label className="flex min-w-0 flex-1 cursor-pointer items-center gap-3">
                <Checkbox
                  checked={step.done}
                  disabled={busy === step.key}
                  onCheckedChange={() => void toggle(step)}
                  aria-label={meta.label}
                />
                <span className="flex min-w-0 flex-col leading-tight">
                  <span className={step.done ? "text-muted-foreground line-through" : ""}>
                    {meta.label}
                  </span>
                  {meta.hint && <span className="text-muted-foreground text-xs">{meta.hint}</span>}
                </span>
              </label>
              {meta.to && (
                <Link
                  to={meta.to(clientId)}
                  className="text-info shrink-0"
                  aria-label={`Open: ${meta.label}`}
                >
                  <ArrowRight className="size-4" />
                </Link>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
